/** @format */
/** @format */

import React, { FC, useState, useEffect } from 'react'
import styled from 'styled-components'
import {
  _$,
  Select,
  Option,
  Icon,
  Table,
  TableColumn,
  Pagination,
  Tag,
  Message,
  Ellipsis,
  dayjs
} from '@king-design/react'
import QuickRangeDate from 'src/components/QuickRangeDate'
import {
  MonitorSearch,
  DeleteConfirm,
  TablePage,
  TableSearch,
  getURLTableSearchParams
} from 'src/components'
import { PrometheusAlarmTabProps } from 'src/pages/PrometheusMonitor/Alarm'
import { PageProps, initPage } from 'src/utils/interface'
import { useHistory } from 'react-router-dom'
import { useAsyncFn } from 'react-use'
import { getPrometheusAlertNotific, getPrometheusAlertPolicyList } from 'src/api/ApiPrometheusAlarm'
import { merge, unset } from 'lodash'
import { notificationResultScheme } from 'src/components/AlarmReciver/new'
import { urlNoticeResultScheme } from 'src/components/AlarmCallBack/new'

interface SearchProps extends PageProps {
  Instance: string
  PolicyName: string
  RecordType: 0 | 1 | 2
  Time: [number, number] | []
}

const initSearch: SearchProps = {
  Instance: '',
  PolicyName: '',
  RecordType: 0,
  Time: [dayjs().subtract(7, 'day').valueOf(), dayjs().valueOf()],
  ...initPage
}

const NotificationTypeEnum = {
  0: '全部',
  1: '触发',
  2: '恢复'
}
// QuickRangeDate depends on fixed Chinese preset labels.
const quickRangeOptions = [
  '近5分钟',
  '近0.5小时',
  '近1小时',
  '近3小时',
  '近1天',
  '近3天',
  '近1周',
  '近30天'
]
const PrometheusAlarmNotificRecord: FC<PrometheusAlarmTabProps> = ({
  className,
  region,
  instances
}) => {
  const history = useHistory()
  const [search, setSearch] = useState<SearchProps>(
    getURLTableSearchParams() || {
      ...initSearch,
      Time: [dayjs().subtract(7, 'day').valueOf(), dayjs().valueOf()]
    }
  )
  const [{ value: tableData }, fetch] = useAsyncFn(
    async (params?) => {
      const mergeParams = merge({}, search, params)
      mergeParams.StartTime = mergeParams.Time[0]
      mergeParams.EndTime = mergeParams.Time[1]
      mergeParams.Region = region
      unset(mergeParams, 'Time')
      if (mergeParams.StartTime && mergeParams.EndTime) {
        const result = await getPrometheusAlertNotific(mergeParams)
        return {
          data: result?.Records || [],
          total: result?.TotalCount || 0
        }
      }
    },
    [search, region]
  )

  useEffect(() => {
    fetch()
  }, [search, region])

  const toPolicyPage = async (policyID, ruleName?) => {
    if (policyID) {
      const result = await getPrometheusAlertPolicyList({
        PolicyID: parseInt(policyID)
      })
      if (result?.Polices?.length) {
        let url = `/monitor/prometheusmonitor/alarm/edit?tab=notific&page=policy&policyid=${policyID}`
        if (ruleName) {
          url += `&rulename=${ruleName}`
        }
        history.push(url)
      } else {
        Message.error(_$('策略不存在'))
      }
    } else {
      Message.error(_$('策略ID为空'))
    }
  }

  return (
    <div className={className}>
      <TableSearch params={search}>
        <div className="search_item">
          <p className="search_item_label">{_$('发送时间')}</p>
          <QuickRangeDate
            realoadIcon={false}
            format="YYYY-MM-DD HH:mm"
            optionalDateRange={90}
            dateDiff={31}
            defautlValue={search.Time}
            width="290px"
            quickRange={quickRangeOptions}
            changeValue={date => {
              setSearch({
                ...search,
                Page: 1,
                Time: date
              })
            }}
          />
          <Icon
            className="ion-refresh cursor-pointer"
            onClick={() => {
              setSearch({
                ...search,
                Time: [dayjs().subtract(7, 'day').valueOf(), dayjs().valueOf()],
                Page: 1
              })
            }}
          />
        </div>
        <div className="search_item">
          <p className="search_item_label">{_$('关联资源')}</p>
          <Select
            value={search.Instance}
            clearable
            filterable
            onChangeValue={value => {
              setSearch({
                ...search,
                Page: 1,
                Instance: value
              })
            }}
          >
            {instances?.map(item => (
              <Option key={item.id} value={item.id}>
                {item.name}
              </Option>
            ))}
          </Select>
        </div>
        <div className="search_item">
          <p className="search_item_label">{_$('通知类型')}</p>
          <Select
            value={search.RecordType}
            onChangeValue={value => {
              setSearch({
                ...search,
                Page: 1,
                RecordType: value
              })
            }}
          >
            {Object.entries(NotificationTypeEnum).map(([value, name]) => {
              return (
                <Option key={value} value={parseInt(value)}>
                  {_$(name)}
                </Option>
              )
            })}
          </Select>
        </div>
        <div className="search_item">
          <p className="search_item_label">{_$('策略名称')}</p>
          <MonitorSearch
            placeholder={_$('请输入策略名称，支持模糊搜索')}
            value={search.PolicyName}
            onChange={value => {
              setSearch({
                ...search,
                Page: 1,
                PolicyName: value
              })
            }}
          />
        </div>
      </TableSearch>
      <Table
        data={tableData?.data || []}
        animation={false}
        rowKey={data => data.ID}
        checkType="none"
      >
        <TableColumn
          key="InstanceID"
          title={_$('关联资源')}
          slotTemplate={([data]) => {
            return data.InstanceID ? (
              <>
                <p>{data.InstanceName}</p>
                <Ellipsis className="table_text" maxLines={2} hoverable>
                  {data.InstanceID}
                </Ellipsis>
              </>
            ) : null
          }}
        ></TableColumn>
        <TableColumn
          key="RuleName"
          title={_$('策略 / 规则')}
          slotTemplate={([data]) => {
            return (
              <a
                onClick={() => {
                  toPolicyPage(data.PolicyID, data.RuleName)
                }}
              >{`${data.PolicyName} / ${data.RuleName}`}</a>
            )
          }}
        />
        <TableColumn
          key="RecordType"
          title={_$('通知类型')}
          width="100"
          slotTemplate={([data]) => {
            return data.RecordType === 1 ? (
              <Tag type="danger">{_$('触发')}</Tag>
            ) : (
              <Tag type="default">{_$('恢复')}</Tag>
            )
          }}
        ></TableColumn>
        <TableColumn
          key="Labels"
          title={_$('标签')}
          slotTemplate={([data]) => {
            if (!data.Labels || !Array.isArray(data.Labels)) return '-'
            return (
              <div className="label-tags">
                {data.Labels.map(label => {
                  if (label.Name === 'severity') {
                    return (
                      <p key={label.Name}>
                        {_$('告警等级（severity）')}= {label.Value}
                      </p>
                    )
                  } else {
                    return (
                      <p key={label.Name}>
                        {label.Name} = {label.Value}
                      </p>
                    )
                  }
                })}
              </div>
            )
          }}
        ></TableColumn>
        <TableColumn
          key="Summary"
          title={_$('告警摘要')}
          slotTemplate={([data]) => {
            return (
              <Ellipsis maxLines={2} hoverable>
                {data.Summary}
              </Ellipsis>
            )
          }}
        ></TableColumn>
        <TableColumn
          key="Description"
          title={_$('告警内容')}
          slotTemplate={([data]) => {
            return (
              <Ellipsis maxLines={2} hoverable>
                {data.Description}
              </Ellipsis>
            )
          }}
        ></TableColumn>

        <TableColumn
          key="Recipients"
          title={_$('通知对象')}
          width="100"
          slotTemplate={([data]) => {
            const { NotificationTargets } = data
            const { Recipients = [], Webhooks = [] } = NotificationTargets || {}
            return Recipients?.length || Webhooks?.length ? (
              <div className="ksc-space">
                {Recipients?.length ? (
                  <DeleteConfirm
                    title={_$('联系人')}
                    content={
                      <>
                        <TablePage
                          tableProps={{
                            checkType: 'none',
                            rowKey: data => data.ContactID,
                            scheme: notificationResultScheme,
                            data: Recipients
                          }}
                        ></TablePage>
                      </>
                    }
                  >
                    <a>{_$('联系人')}</a>
                  </DeleteConfirm>
                ) : null}
                {Webhooks?.length ? (
                  <DeleteConfirm
                    title={_$('回调')}
                    content={
                      <TablePage
                        tableProps={{
                          checkType: 'none',
                          rowKey: data => data.ContactID,
                          scheme: urlNoticeResultScheme,
                          data: Webhooks
                        }}
                      ></TablePage>
                    }
                  >
                    <a>{_$('回调')}</a>
                  </DeleteConfirm>
                ) : null}
              </div>
            ) : (
              '-'
            )
          }}
        ></TableColumn>
        <TableColumn key="NotifiedAt" title={_$('发送时间')} width="100"></TableColumn>
      </Table>
      <Pagination
        value={search.Page}
        limit={search.PageSize}
        total={tableData?.total || 0}
        size="small"
        onChangeValue={value => {
          setSearch({
            ...search,
            Page: value
          })
        }}
        onChangeLimit={limit => {
          setSearch({
            ...search,
            PageSize: limit
          })
        }}
      />
    </div>
  )
}

export default styled(PrometheusAlarmNotificRecord)`
  .ion-refresh {
    margin-left: 4px;
  }
`
